// @ts-nocheck
/* eslint-disable */
//
// Annual Wellness Summary (AWS) PDF generator.
//
// Sibling to generateReportPDF in KnowMenoApp.tsx — NOT an extension of it.
// The Symptom Intelligence Report uses milestone-arc framing (perimenopause → HRT trial →
// 90d snapshot). The AWS is a postmenopause "yearly check-in" snapshot.
//
// Shared with the Symptom Intelligence Report:
//   - PDF_STYLES (./pdfStyles)              — brand colors + page-break rules
//   - statusFor (./screeningStatus)         — screening overdue/due/etc
//   - fetchMenoScoreHistory (./menoScore)   — 365-day score series
//   - reportData helpers (buildSymptomDirections, buildMoodSummary,
//     summarizeHotFlashEvents) — same 90d math
//
// This generator does NOT touch the Symptom Intelligence Report path. The Care-tab UI
// wiring lands in a follow-up session; for now this is a standalone
// callable that's tested via the browser console / a dev button.

import { supabase } from "@/integrations/supabase/client";
import {
  fetchLogsBetween,
  fetchHotFlashEventsBetween,
  fetchHrtBetween,
  fetchMoodsBetween,
  localDateKey,
  parseLocalDateKey,
  EVENT_SYMPTOMS,
} from "./dailyLogs";
import { fetchMenoScoreHistory, MIN_LOGGED_FOR_INCLUSION, type DailyScore } from "./menoScore";
import { isOnHrt, getHrtName, getHrtStartDate } from "./hrtStatus";
import { formatHrtSchedule } from "./hrtSchedule";
import { fetchReportData, buildSymptomDirections, buildMoodSummary, summarizeHotFlashEvents, buildDataConfidence, renderSymptomHeatmapSvg, type DataConfidence } from "./reportData";
import { buildTriggerReportData, buildTriggerDiscussionPoints, type TriggerReportData, type TriggerComparison } from "./triggerCorrelations";
import { LAB_MARKERS, getRange, DISCLAIMER, type MarkerKey, type LifeStage } from "./labRanges";
import { groupByMarker, computeTrend, type LabUploadRow } from "./labTrends";
import { type HealthScreeningRow } from "@/lib/screenings.functions";
import { prepareReportWindow, finalizeReportWindow } from "./openReportWindow";
import { statusFor, type ScreeningStatus } from "./screeningStatus";
import { PDF_STYLES } from "./pdfStyles";
import {
  loadVitalityScoreForCurrentUser,
  loadVitalityScoreSeriesForCurrentUser,
  type VitalityScore,
  type VitalityScoreSeriesPoint,
} from "./vitalityScore";
import { fetchWearableMetrics, summarizeForToday, fetchWearableStress, stressLabelFor, type WearableTodaySummary } from "./wearableMetrics";
import { fitnessAgeFromVO2max, ageFromDob, type FitnessAgeResult } from "./fitnessAge";

// ---- Brand palette (string copies of the CSS vars for inline SVG/HTML
// where CSS variables wouldn't resolve, e.g. inside SVG `stroke=`). Keep
// in sync with src/knowmeno/pdfStyles.ts.
const PD_PLUM = "#4B1F4F";
const PD_SOFT_PLUM = "#856589";
const PD_CORAL = "#FF5C7A";
const PD_PINK = "#FFC1CD";
const PD_WARM_TAUPE = "#E8D5DA";
const PD_MUTED_PURPLE = "#6E4B71";

const STATUS_PILL: Record<ScreeningStatus, { text: string; bg: string; color: string }> = {
  up_to_date:   { text: "Up to date", bg: "#DCEFE2", color: "#1F6F3D" },
  due_soon:     { text: "Due soon",   bg: "#FFE3D1", color: "#A04A12" },
  overdue:      { text: "Overdue",    bg: "#FFD0D9", color: PD_CORAL  },
  never_logged: { text: "Not logged", bg: "#EFEAEC", color: PD_MUTED_PURPLE },
};

const SCREENING_LABEL: Record<string, string> = {
  mammogram: "Mammogram",
  dexa: "Bone density (DEXA)",
  lipid_panel: "Lipid panel",
  a1c: "A1C / blood sugar",
  colonoscopy: "Colonoscopy",
  pap_hpv: "Pap / HPV",
  blood_pressure: "Blood pressure",
  eye_exam: "Eye exam",
};

function fmtLongDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = iso.length === 10 ? new Date(iso + "T00:00:00") : new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function emptyNotice(msg: string): string {
  return `<div class="empty-state-notice">${escapeHtml(msg)}</div>`;
}

function escapeHtml(s: string): string {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// ============================================================
// Section 2 — Vitality 12-month SVG line chart
// ============================================================
function svgVitalityChart(series: DailyScore[]): string {
  // Eligibility filter (audit fix #1): mirror computeSmoothedHeadline so all
  // averaged surfaces agree on "what counts as a real day" — a day must have
  // at least MIN_LOGGED_FOR_INCLUSION symptoms logged to enter any average.
  const scored = series.filter(s => s.score != null && s.loggedCount >= MIN_LOGGED_FOR_INCLUSION) as { dateKey: string; score: number }[];
  const W = 680, H = 200;
  const padL = 32, padR = 14, padT = 12, padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  if (scored.length === 0) {
    return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block;background:#fff;border:0.5px solid ${PD_WARM_TAUPE};border-radius:8px;"></svg>`;
  }
  const yMin = 0, yMax = 100;
  const n = series.length;
  const xFor = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * innerW);
  const yFor = (v: number) => padT + (1 - (v - yMin) / (yMax - yMin)) * innerH;

  // Gridlines at 0/25/50/75/100
  const grid = [0, 25, 50, 75, 100].map(v => {
    const y = yFor(v);
    return `<line x1="${padL}" y1="${y}" x2="${W - padR}" y2="${y}" stroke="${PD_WARM_TAUPE}" stroke-width="1"/><text x="${padL - 6}" y="${y + 3}" font-size="9" fill="${PD_MUTED_PURPLE}" text-anchor="end">${v}</text>`;
  }).join("");

  // Month tick labels — find first-of-month positions inside the window.
  const monthTicks: string[] = [];
  let lastMonthLabel = "";
  for (let i = 0; i < n; i++) {
    const d = parseLocalDateKey(series[i].dateKey);
    if (!d) continue;
    const label = d.toLocaleDateString("en-US", { month: "short" });
    if (label !== lastMonthLabel && d.getDate() <= 7) {
      monthTicks.push(`<text x="${xFor(i)}" y="${H - 8}" font-size="9" fill="${PD_MUTED_PURPLE}" text-anchor="middle">${label}</text>`);
      lastMonthLabel = label;
    }
  }

  // Polyline — skip null days (break the line).
  const segs: string[][] = [[]];
  const dots: string[] = [];
  for (let i = 0; i < n; i++) {
    const v = series[i].score;
    if (v == null) {
      if (segs[segs.length - 1].length) segs.push([]);
      continue;
    }
    segs[segs.length - 1].push(`${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`);
  }
  // Dot for latest scored entry only (keeps chart clean over 365 days).
  const lastScoredIdx = (() => {
    for (let i = n - 1; i >= 0; i--) if (series[i].score != null) return i;
    return -1;
  })();
  if (lastScoredIdx >= 0) {
    dots.push(`<circle cx="${xFor(lastScoredIdx).toFixed(1)}" cy="${yFor(series[lastScoredIdx].score!).toFixed(1)}" r="3" fill="${PD_CORAL}"/>`);
  }
  const polys = segs.filter(s => s.length > 1).map(s =>
    `<polyline fill="none" stroke="${PD_CORAL}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" points="${s.join(" ")}"/>`,
  ).join("");

  return `
    <svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block;background:#fff;border:0.5px solid ${PD_WARM_TAUPE};border-radius:8px;">
      ${grid}
      ${monthTicks.join("")}
      ${polys}
      ${dots.join("")}
    </svg>`;
}

function vitalitySection(series: DailyScore[]): string {
  const scored = series.filter(s => s.score != null && s.loggedCount >= MIN_LOGGED_FOR_INCLUSION) as { dateKey: string; score: number }[];
  if (scored.length < 30) {
    return `
      <div class="section">
        <h2>Vitality — 12 Months at a Glance</h2>
        ${emptyNotice(`Your 12-month Vitality view will populate as you continue daily check-ins. Currently showing data from ${scored.length} day${scored.length === 1 ? "" : "s"} of tracking.`)}
      </div>`;
  }
  const current = scored[scored.length - 1].score;
  const avg = Math.round(scored.reduce((s, p) => s + p.score, 0) / scored.length);
  let hi = scored[0], lo = scored[0];
  for (const p of scored) {
    if (p.score > hi.score) hi = p;
    if (p.score < lo.score) lo = p;
  }
  const stat = (label: string, value: string, sub?: string) => `
    <div class="kpi" style="flex:1;min-width:0;">
      <div class="kpi-big">${escapeHtml(value)}</div>
      <div style="font-size:11px;color:${PD_SOFT_PLUM};text-transform:uppercase;letter-spacing:0.04em;">${escapeHtml(label)}</div>
      ${sub ? `<div style="font-size:11px;color:${PD_MUTED_PURPLE};margin-top:3px;">${escapeHtml(sub)}</div>` : ""}
    </div>`;
  return `
    <div class="section">
      <h2>Vitality — 12 Months at a Glance</h2>
      ${svgVitalityChart(series)}
      <div style="display:flex;gap:10px;margin-top:12px;">
        ${stat("Today", String(current))}
        ${stat("12-month average", String(avg))}
        ${stat("Range", `${lo.score} – ${hi.score}`, `Low ${fmtLongDate(lo.dateKey)} · High ${fmtLongDate(hi.dateKey)}`)}
      </div>
    </div>`;
}

// ============================================================
// Recovery — 14-day sparkline (same math as svgVitalityChart,
// but day-window ticks instead of month ticks)
// ============================================================
function svgRecoverySparkline(series: VitalityScoreSeriesPoint[]): string {
  const W = 680, H = 120;
  const padL = 32, padR = 14, padT = 10, padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const n = series.length;
  if (n === 0) {
    return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block;background:#fff;border:0.5px solid ${PD_WARM_TAUPE};border-radius:8px;"></svg>`;
  }
  const yMin = 0, yMax = 100;
  const xFor = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * innerW);
  const yFor = (v: number) => padT + (1 - (v - yMin) / (yMax - yMin)) * innerH;

  const grid = [0, 50, 100].map(v => {
    const y = yFor(v);
    return `<line x1="${padL}" y1="${y}" x2="${W - padR}" y2="${y}" stroke="${PD_WARM_TAUPE}" stroke-width="1"/><text x="${padL - 6}" y="${y + 3}" font-size="9" fill="${PD_MUTED_PURPLE}" text-anchor="end">${v}</text>`;
  }).join("");

  // Day ticks: first, middle, last
  const tickIdx = n === 1 ? [0] : n === 2 ? [0, 1] : [0, Math.floor((n - 1) / 2), n - 1];
  const dayTicks = tickIdx.map(i => {
    const d = parseLocalDateKey(series[i].dateKey);
    if (!d) return "";
    const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `<text x="${xFor(i)}" y="${H - 6}" font-size="9" fill="${PD_MUTED_PURPLE}" text-anchor="middle">${label}</text>`;
  }).join("");

  const segs: string[][] = [[]];
  const dots: string[] = [];
  for (let i = 0; i < n; i++) {
    const v = series[i].score;
    if (v == null) {
      if (segs[segs.length - 1].length) segs.push([]);
      continue;
    }
    segs[segs.length - 1].push(`${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`);
  }
  const lastScoredIdx = (() => {
    for (let i = n - 1; i >= 0; i--) if (series[i].score != null) return i;
    return -1;
  })();
  if (lastScoredIdx >= 0) {
    dots.push(`<circle cx="${xFor(lastScoredIdx).toFixed(1)}" cy="${yFor(series[lastScoredIdx].score!).toFixed(1)}" r="3" fill="${PD_CORAL}"/>`);
  }
  const polys = segs.filter(s => s.length > 1).map(s =>
    `<polyline fill="none" stroke="${PD_CORAL}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" points="${s.join(" ")}"/>`,
  ).join("");

  return `
    <svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block;background:#fff;border:0.5px solid ${PD_WARM_TAUPE};border-radius:8px;">
      ${grid}
      ${dayTicks}
      ${polys}
      ${dots.join("")}
    </svg>`;
}

// ============================================================
// Recovery section — mirrors RecoverySection in VitalityReportCard.
// Honest empty/building states; never zeros or fabricated values.
// ============================================================
function recoverySection(args: { vitality: VitalityScore | null; series: VitalityScoreSeriesPoint[]; stress: any }): string {
  const { vitality, series, stress } = args;
  if (!vitality) {
    return `
      <div class="section">
        <h2>Recovery</h2>
        ${emptyNotice("Recovery signals aren't available yet. Connect a wearable that reports sleep, HRV, and resting heart rate to see your daily 0–100 Vitality Score.")}
      </div>`;
  }
  if (vitality.status !== "ok" || vitality.score == null) {
    return `
      <div class="section">
        <h2>Recovery</h2>
        ${emptyNotice(vitality.lead || "Your recovery score is building. It needs a couple of weeks of nightly data to establish your baseline.")}
      </div>`;
  }
  const band = vitality.band;
  const top = vitality.contributions?.[0];
  const bandPill = band
    ? `<span style="display:inline-block;font-size:11px;font-weight:600;color:${PD_PLUM};background:${band.color};padding:3px 10px;border-radius:999px;margin-left:10px;vertical-align:middle;">${escapeHtml(band.label)}</span>`
    : "";
  const driver = top
    ? `<div style="font-size:11px;color:${PD_MUTED_PURPLE};margin-top:6px;">Top driver — ${escapeHtml(top.label)}: ${escapeHtml(top.detail)}</div>`
    : "";
  const stressLine = (stress && typeof stress.n === "number" && stress.n > 0 && stress.latest?.value != null)
    ? `<div style="font-size:13px;color:${PD_PLUM};margin-top:10px;line-height:1.5;"><strong>Stress (derived from HRV)</strong> · ${escapeHtml(stressLabelFor(stress.latest.value))} (avg ${Number(stress.avg).toFixed(1)}/4 over last 30 days)</div>
       <div style="font-size:11px;color:${PD_SOFT_PLUM};margin-top:4px;">Estimated from overnight HRV vs the patient's 7-day baseline — not a direct sensor reading.</div>`
    : "";
  return `
    <div class="section">
      <h2>Recovery${bandPill}</h2>
      <div style="display:flex;gap:18px;align-items:flex-start;margin-bottom:10px;">
        <div class="kpi" style="flex:0 0 auto;">
          <div class="kpi-big">${vitality.score}</div>
          <div style="font-size:11px;color:${PD_SOFT_PLUM};text-transform:uppercase;letter-spacing:0.04em;">Today · /100</div>
        </div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:13px;color:${PD_PLUM};line-height:1.5;">${escapeHtml(vitality.lead || "")}</div>
          ${driver}
        </div>
      </div>
      ${stressLine}
      ${series.length >= 2 ? svgRecoverySparkline(series) : emptyNotice("14-day trend will appear once at least two nights of data are recorded.")}
    </div>`;
}

// ============================================================
// Longevity section — VO2max + Fitness Age. Mirrors LongevitySection.
// Honest empty states: never fabricate values.
// ============================================================
function longevitySection(args: { user: any; summary: WearableTodaySummary | null }): string {
  const { user, summary } = args;
  const vo2 = summary?.vo2Max?.value ?? null;
  const age = ageFromDob(user?.dob);
  let fa: FitnessAgeResult | null = null;
  if (vo2 != null && age != null) fa = fitnessAgeFromVO2max(vo2, age);

  const vo2Sub = vo2 != null ? "ml/kg/min" : "Connect a wearable that reports VO2max";
  const faValue = fa ? `${fa.fitnessAge} yrs` : "—";
  // NOTE: deltaYears sign follows VitalityReportCard convention.
  // Sign verification deferred to Step 6 before writing the Longevity
  // discussion rule (user flagged earlier fitness-age direction-flip bug).
  const faSub = fa
    ? (fa.deltaYears === 0
        ? "Matches your chronological age"
        : fa.deltaYears > 0
          ? `${fa.deltaYears} yrs younger than your age`
          : `${Math.abs(fa.deltaYears)} yrs older than your age`)
    : vo2 == null
      ? "Needs VO2max from a connected wearable"
      : age == null
        ? "Add your birth year in Settings"
        : "—";

  const tile = (label: string, value: string, sub: string) => `
    <div class="kpi" style="flex:1;min-width:0;">
      <div class="kpi-big">${escapeHtml(value)}</div>
      <div style="font-size:11px;color:${PD_SOFT_PLUM};text-transform:uppercase;letter-spacing:0.04em;">${escapeHtml(label)}</div>
      <div style="font-size:11px;color:${PD_MUTED_PURPLE};margin-top:3px;">${escapeHtml(sub)}</div>
    </div>`;

  return `
    <div class="section">
      <h2>Longevity</h2>
      <div style="display:flex;gap:10px;">
        ${tile("VO2max", vo2 != null ? vo2.toFixed(1) : "—", vo2Sub)}
        ${tile("Fitness age", faValue, faSub)}
      </div>
    </div>`;
}

// ============================================================
// Healthspan — single section mirroring VitalityReportCard's
// HealthspanSection (Labs + Screenings sub-tiles), 1:1.
// Defined but NOT yet wired into sectionsHtml — wiring lands in Step 6.
// ============================================================
function healthspanSection(args: {
  user: any;
  labUploads: LabUploadRow[];
  screenings: HealthScreeningRow[];
}): string {
  const { user, labUploads, screenings } = args;
  const stage = (user?.stage || user?.lifeStage || "Postmenopause") as LifeStage;
  const onHrt = isOnHrt(user);
  const hrtType = user?.hrtType;

  // Local maps copied VERBATIM from VitalityReportCard.tsx (single source: the card).
  const HS_SCREENING_LABEL: Record<string, string> = {
    mammogram: "Mammogram",
    dexa: "DEXA scan",
    lipid_panel: "Lipid panel",
    a1c: "A1c",
    colonoscopy: "Colonoscopy",
    pap_hpv: "Pap / HPV",
    blood_pressure: "Blood pressure",
    eye_exam: "Eye exam",
  };
  const HS_STATUS_LABEL: Record<ScreeningStatus, string> = {
    overdue: "Overdue",
    due_soon: "Due soon",
    up_to_date: "Up to date",
    never_logged: "Not logged",
  };
  const HS_STATUS_COLOR: Record<ScreeningStatus, string> = {
    overdue: "#C0392B",
    due_soon: "#C49A1F",
    up_to_date: "#2E7D5B",
    never_logged: "#7A6F73", // matches the card's GR
  };

  // ---------- Labs sub-tile ----------
  const grouped = groupByMarker(labUploads || []);
  const markersWithData = LAB_MARKERS.filter((m) => (grouped[m.key]?.length ?? 0) > 0);
  let labsBody: string;
  if (markersWithData.length === 0) {
    labsBody = `<div style="font-size:12px;color:${PD_MUTED_PURPLE};line-height:1.5;">No labs uploaded yet — add results to see hormone + thyroid trends.</div>`;
  } else {
    labsBody = markersWithData.map((m) => {
      const points = grouped[m.key as MarkerKey];
      const latest = points[points.length - 1];
      // FULL series (ascending parseLocalDateKey via groupByMarker), not last-3.
      const trend = computeTrend(points.map((p) => p.value));
      // Still call getRange with all 4 args so the estradiol-on-HRT fix drives color,
      // even though the displayed range cell is dropped.
      const range = getRange(m.key as MarkerKey, stage, onHrt, hrtType);
      const inRange =
        (range.low == null || latest.value >= range.low) &&
        (range.high == null || latest.value <= range.high);
      const valueColor = inRange ? "#2E7D5B" : "#C49A1F";
      // Card glyph: ↑ ↓ → "" (empty for "none"), NOT em-dash.
      const arrow = trend === "up" ? "↑" : trend === "down" ? "↓" : trend === "stable" ? "→" : "";
      const displayUnit = m.key === "tsh" ? m.unit : (latest.unit || m.unit);
      return `<div style="display:flex;justify-content:space-between;font-size:12px;color:${PD_PLUM};margin-bottom:4px;">
        <span style="font-weight:500;">${escapeHtml(m.displayName)}</span>
        <span style="color:${valueColor};">${latest.value} ${escapeHtml(displayUnit)}${arrow ? ` ${arrow}` : ""}</span>
      </div>`;
    }).join("");
  }

  // ---------- Screenings sub-tile ----------
  const order: ScreeningStatus[] = ["overdue", "due_soon", "up_to_date", "never_logged"];
  const sortedScreenings = [...(screenings || [])].sort(
    (a, b) => order.indexOf(statusFor(a)) - order.indexOf(statusFor(b))
  );
  let screeningsBody: string;
  if (sortedScreenings.length === 0) {
    screeningsBody = `<div style="font-size:12px;color:${PD_MUTED_PURPLE};line-height:1.5;">No screenings logged yet.</div>`;
  } else {
    screeningsBody = sortedScreenings.slice(0, 6).map((s) => {
      const st = statusFor(s);
      const name = HS_SCREENING_LABEL[s.screening_type] || s.screening_type;
      return `<div style="display:flex;justify-content:space-between;font-size:12px;color:${PD_PLUM};margin-bottom:4px;">
        <span style="font-weight:500;">${escapeHtml(name)}</span>
        <span style="color:${HS_STATUS_COLOR[st]};">${HS_STATUS_LABEL[st]}</span>
      </div>`;
    }).join("");
  }

  const subTileStyle = `background:#FAF7F5;border-radius:12px;padding:12px 14px;`;
  const subHeadStyle = `font-size:11px;color:${PD_SOFT_PLUM};letter-spacing:0.06em;text-transform:uppercase;font-weight:500;margin-bottom:8px;`;

  return `
    <div class="section">
      <h2>Healthspan</h2>
      <div style="display:flex;flex-direction:column;gap:10px;">
        <div style="${subTileStyle}">
          <div style="${subHeadStyle}">Labs</div>
          ${labsBody}
        </div>
        <div style="${subTileStyle}">
          <div style="${subHeadStyle}">Screenings</div>
          ${screeningsBody}
        </div>
      </div>
      <!-- PENDING REVIEW — may drop. Disclaimer retained verbatim until user decides keep-or-drop. -->
      <div class="note" style="font-size:11px;margin-top:10px;">${escapeHtml(DISCLAIMER)}</div>
    </div>`;
}



// ============================================================
// Section 3 — Health Screenings
// ============================================================
function screeningsSection(rows: HealthScreeningRow[]): string {
  if (!rows || rows.length === 0) {
    return `
      <div class="section">
        <h2>Screenings</h2>
        ${emptyNotice("No screenings logged yet. Talk to your provider about your screening schedule and log them in your profile to see them tracked here.")}
      </div>`;
  }
  const body = rows.map(r => {
    const st = statusFor(r);
    const pill = STATUS_PILL[st];
    const name = SCREENING_LABEL[r.screening_type] || r.screening_type;
    return `<tr>
      <td style="font-weight:500;">${escapeHtml(name)}</td>
      <td>${escapeHtml(fmtLongDate(r.last_done_date) || "Not yet logged")}</td>
      <td>${escapeHtml(fmtLongDate(r.next_due_date) || "Not scheduled")}</td>
      <td>${escapeHtml(r.provider || "—")}</td>
      <td><span class="flag" style="background:${pill.bg};color:${pill.color};">${pill.text}</span></td>
    </tr>`;
  }).join("");
  return `
    <div class="section">
      <h2>Screenings</h2>
      <table>
        <thead><tr><th>Screening</th><th>Last done</th><th>Next due</th><th>Provider</th><th>Status</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
      <p class="note" style="font-size:11px;margin-top:8px;">Next-due dates are based on standard intervals — your doctor may set a different schedule based on your history.</p>
    </div>`;
}

// ============================================================
// Section 4 — Lab Trends
// ============================================================
function labTrendsSection(labUploads: LabUploadRow[], stage: string, onHRT: boolean, hrtType: string | undefined): string {
  const grouped = groupByMarker(labUploads || []);
  const present = LAB_MARKERS.filter(m => (grouped[m.key]?.length || 0) > 0);
  if (present.length === 0) {
    return `
      <div class="section">
        <h2>Lab Trends</h2>
        ${emptyNotice("Lab results will appear here once you upload bloodwork from your provider.")}
      </div>`;
  }
  const arrow = (t: ReturnType<typeof computeTrend>) => t === "up" ? "↑" : t === "down" ? "↓" : t === "stable" ? "→" : "—";
  const arrowColor = (t: ReturnType<typeof computeTrend>) => t === "up" ? PD_CORAL : t === "down" ? "#1F6F3D" : PD_SOFT_PLUM;
  const body = present.map(m => {
    const pts = grouped[m.key];
    const last3 = pts.slice(-3);
    const trend = computeTrend(last3.map(p => p.value));
    const range = getRange(m.key as MarkerKey, stage as any, onHRT, hrtType);
    const rangeText = range.low != null && range.high != null
      ? `${range.low}–${range.high} ${m.unit}`
      : range.low != null
        ? `≥ ${range.low} ${m.unit}`
        : range.high != null
          ? `≤ ${range.high} ${m.unit}`
          : "—";
    const valuesCell = last3.map(p => {
      const d = new Date(p.date);
      const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      // TSH stored unit ("µIU/mL") and reference unit ("mIU/L") are numerically
      // identical (1:1) — normalize the displayed unit to the reference so the
      // value and reference labels match. Do NOT do this for other markers.
      const displayUnit = m.key === "tsh" ? m.unit : (p.unit || m.unit);
      return `<div style="font-size:12px;">${p.value} ${escapeHtml(displayUnit)} <span style="color:${PD_MUTED_PURPLE};">· ${label}</span></div>`;
    }).join("");

    return `<tr>
      <td style="font-weight:500;vertical-align:top;">${escapeHtml(m.displayName)}<div style="font-size:11px;color:${PD_MUTED_PURPLE};font-weight:400;">${escapeHtml(m.description)}</div></td>
      <td>${valuesCell}</td>
      <td style="font-size:18px;color:${arrowColor(trend)};font-weight:600;text-align:center;">${arrow(trend)}</td>
      <td style="font-size:12px;">${escapeHtml(rangeText)}<div style="font-size:11px;color:${PD_MUTED_PURPLE};">${escapeHtml(range.displayContext)}</div></td>
    </tr>`;
  }).join("");
  return `
    <div class="section">
      <h2>Lab Trends</h2>
      <table>
        <thead><tr><th>Marker</th><th>Last 3 values</th><th>Trend</th><th>Reference</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
      <div class="note" style="font-size:11px;">${escapeHtml(DISCLAIMER)}</div>
    </div>`;
}

// ============================================================
// Section 5 — 90-day Symptom Snapshot
// ============================================================
function symptomSnapshotSection(args: {
  directions: ReturnType<typeof buildSymptomDirections>;
  mood: ReturnType<typeof buildMoodSummary>;
  hf: ReturnType<typeof summarizeHotFlashEvents>;
}): string {
  const { directions, mood, hf } = args;
  const rows = directions.directions
    .filter(d => d.lastDays > 0)
    .map(d => `<tr>
      <td style="font-weight:500;">${escapeHtml(d.name)}</td>
      <td>${d.lastDays} day${d.lastDays === 1 ? "" : "s"}</td>
      <td>${d.lastAvg ? d.lastAvg.toFixed(1) : "—"} / 4</td>
      <td style="color:${d.dir === "improving" ? "#1F6F3D" : d.dir === "worsening" ? PD_CORAL : PD_SOFT_PLUM};">${d.dir}</td>
    </tr>`).join("");
  const symptomsEmpty = !rows;
  const symptomBlock = rows
    ? `<table>
        <thead><tr><th>Symptom</th><th>Days logged</th><th>Avg severity</th><th>Direction</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`
    : emptyNotice("No symptom entries tracked in the last 90 days.");

  // Hot flashes summary block.
  const hfEmpty = !(hf.total > 0);
  const hfBlock = !hfEmpty
    ? `<p style="margin:12px 0 0;font-size:13px;line-height:1.55;">
        <strong>Hot flashes:</strong> ${hf.total} events across ${hf.daysWithEvent} day${hf.daysWithEvent === 1 ? "" : "s"} (median ${hf.medianPerDay}/day).${hf.peakHourBand ? ` Peak window: ${escapeHtml(hf.peakHourBand)}.` : ""}
      </p>`
    : emptyNotice("Hot flash events: no entries tracked. Patient may experience symptoms that aren't being logged.");

  // Mood distribution bar.
  let moodBlock = "";
  let moodEmpty = false;
  if (mood.state === "ready" || mood.state === "caveat") {
    const dist = mood.distribution || [];
    const segments = dist.map(d => {
      const color = ["#9BD0AA", "#C7E3CD", "#E5DAA9", "#F0B3A6", "#D77A9C"][d.mood] || PD_PINK;
      return `<div title="${escapeHtml(d.label)} ${d.pct}%" style="background:${color};width:${d.pct}%;height:18px;"></div>`;
    }).join("");
    const legend = dist
      .filter(d => d.pct > 0)
      .map(d => `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:10px;font-size:11px;color:${PD_PLUM};"><span style="display:inline-block;width:8px;height:8px;background:${["#9BD0AA", "#C7E3CD", "#E5DAA9", "#F0B3A6", "#D77A9C"][d.mood] || PD_PINK};border-radius:2px;"></span>${escapeHtml(d.label)} ${d.pct}%</span>`)
      .join("");
    moodBlock = `<div style="margin-top:18px;">
      <h3>Mood Distribution</h3>
      <div style="display:flex;width:100%;border-radius:4px;overflow:hidden;">${segments}</div>
      <div style="margin-top:8px;">${legend}</div>
    </div>`;
  } else {
    moodEmpty = true;
    moodBlock = `<div style="margin-top:18px;">
      <h3>Mood Distribution</h3>
      ${emptyNotice("No mood entries tracked in the last 90 days.")}
    </div>`;
  }

  const allEmpty = symptomsEmpty && hfEmpty && moodEmpty;
  const clinicianDisclaimer = allEmpty
    ? emptyNotice("Note: Absence of logged entries does not confirm absence of symptoms. Please ask the patient directly about any symptoms not reflected here.")
    : "";

  return `
    <div class="section">
      <h2>Recent Symptom Patterns (Last 90 Days)</h2>
      ${clinicianDisclaimer}
      ${symptomBlock}
      ${hfBlock}
      ${moodBlock}
    </div>`;
}


// ============================================================
// Section 6 — HRT Timeline
// ============================================================
function hrtSection(args: { hrtType?: string; hrtStartDate?: string | null; adherencePct: number | null }): string {
  const { hrtType, hrtStartDate, adherencePct } = args;
  if (!hrtType || hrtType === "None / Not started yet") {
    return `
      <div class="section">
        <h2>HRT Timeline</h2>
        <div style="font-size:13px;">Not currently on HRT.</div>
      </div>`;
  }
  const startD = parseLocalDateKey(hrtStartDate || "");
  let durationLine = "";
  if (startD) {
    const months = Math.max(0, Math.floor((Date.now() - startD.getTime()) / (1000 * 60 * 60 * 24 * 30.4375)));
    durationLine = `<div style="font-size:13px;margin-top:4px;">Started: ${escapeHtml(fmtLongDate(hrtStartDate || "")!)} · ${months} month${months === 1 ? "" : "s"} of treatment</div>`;
  } else {
    durationLine = `<div class="empty-state-notice">Start date not on file — add it in your profile to track treatment duration.</div>`;
  }
  const adherence = adherencePct == null
    ? `<div style="font-size:13px;margin-top:8px;color:${PD_SOFT_PLUM};">Adherence: not enough data in the last 90 days.</div>`
    : `<div style="font-size:13px;margin-top:8px;"><strong>90-day adherence:</strong> ${adherencePct}%</div>`;
  return `
    <div class="section">
      <h2>HRT Timeline</h2>
      <div style="font-size:13px;"><strong>Current HRT:</strong> ${escapeHtml(hrtType)}</div>
      ${durationLine}
      ${adherence}
    </div>`;
}

// ------------------------------------------------------------
// Step 5 — card-parity HRT section (UNWIRED; wiring deferred to Step 6).
// Mirrors VitalityReportCard's HrtRow: gates via isOnHrt(user) (fixes the
// "None / Not started yet" string-match sentinel bug), uses getHrtName /
// getHrtStartDate / parseLocalDateKey, no peri interpretation, no HRT-response
// delta. Dose/Schedule rows render only when present (no "Not specified").
// Adherence is the sole PDF-only addendum, conditional on adherencePct != null.
// ------------------------------------------------------------
function hrtSectionLight(args: {
  user: any;
  adherencePct: number | null;
  adherenceDenomDays?: number;
  adherenceStartedDaysAgo?: number | null;
  startedWithinWindow?: boolean;
}): string {
  const { user, adherencePct, adherenceDenomDays, adherenceStartedDaysAgo, startedWithinWindow } = args;
  if (!isOnHrt(user)) {
    return `
      <div class="section">
        <h2>HRT</h2>
        <div style="font-size:13px;">Not currently on HRT.</div>
      </div>`;
  }
  const name = getHrtName(user);
  const startKey = getHrtStartDate(user);
  const startD = parseLocalDateKey(startKey || "");

  let headline: string;
  if (startD) {
    const months = Math.max(0, Math.floor((Date.now() - startD.getTime()) / (1000 * 60 * 60 * 24 * 30.4375)));
    const durationLabel =
      months >= 12
        ? `${(months / 12).toFixed(1).replace(/\.0$/, "")} yr${months >= 24 ? "s" : ""}`
        : `${months} mo${months === 1 ? "" : "s"}`;
    headline = `<strong>${escapeHtml(name || "HRT")}</strong> · On HRT for ${durationLabel}`;
  } else {
    headline = name
      ? `<strong>${escapeHtml(name)}</strong> — start date not recorded.`
      : `On HRT — start date not recorded.`;
  }

  const dose = typeof user?.hrtDose === "string" && user.hrtDose.trim()
    ? `<div style="font-size:13px;margin-top:4px;">Dose: ${escapeHtml(user.hrtDose.trim())}</div>`
    : "";
  const schedule = user?.hrtSchedule
    ? `<div style="font-size:13px;margin-top:4px;">Schedule: ${escapeHtml(formatHrtSchedule(user.hrtSchedule))}</div>`
    : "";

  // Adherence line:
  //  - if pct is null AND started < 14 days ago → "Started X days ago — adherence shown from day 14."
  //  - if pct is set → "{N}-day adherence (since starting HRT|last 90 days): X%"
  //  - else → omit
  let adherence = "";
  if (adherencePct == null) {
    if (typeof adherenceStartedDaysAgo === "number" && adherenceStartedDaysAgo < 14) {
      adherence = `<div style="font-size:13px;margin-top:8px;color:${PD_SOFT_PLUM};">Started ${adherenceStartedDaysAgo} day${adherenceStartedDaysAgo === 1 ? "" : "s"} ago — adherence shown from day 14.</div>`;
    }
  } else {
    const suffix = startedWithinWindow ? "(since starting HRT)" : "(last 90 days)";
    const denom = adherenceDenomDays ?? 90;
    adherence = `<div style="font-size:13px;margin-top:8px;"><strong>${denom}-day adherence ${suffix}:</strong> ${adherencePct}%</div>`;
  }

  return `
    <div class="section">
      <h2>HRT</h2>
      <div style="font-size:13px;">${headline}</div>
      ${dose}
      ${schedule}
      ${adherence}
    </div>`;
}

// ------------------------------------------------------------
// Step 5 — postmeno Symptoms section (UNWIRED; wiring deferred to Step 6).
//
// Reuses buildSymptomDirections (last-30 vs prior-30) and summarizeHotFlashEvents
// (90d window — fetched via fetchHotFlashEventsBetween(last90Start=today-89,
// todayKey) at L809). No recompute. Drops the mood-distribution bar, the
// clinical "Patient may experience…" sentence, and the clinician absence
// notice from the old symptomSnapshotSection. Adds a Days (last / prior)
// column so a direction arrow built on 1–2 days isn't misread as a strong
// trend. Empty-state keeps a plain non-clinical honesty caveat.
//
// Symptom set: caller passes the full set of names actually logged in the
// 60-day window (REPORT_SYMPTOMS is the default for legacy callers, not a
// limit here). Anxiety, Headaches, Joint pain etc. now surface when logged.
// EVENT_SYMPTOMS (hot flashes) are excluded — they have their own block.
// ------------------------------------------------------------
function symptomsSectionLight(args: {
  directions: ReturnType<typeof buildSymptomDirections>;
  hf: ReturnType<typeof summarizeHotFlashEvents>;
  logsByDate: Record<string, Record<string, number>>;
  hfCountByDate: Record<string, number>;
  days: number;
}): string {
  const { directions, hf, logsByDate, hfCountByDate, days } = args;

  const visibleRows = directions.directions.filter(d => d.lastDays > 0 || d.priorDays > 0);
  const fmtAvg = (avg: number, days: number) => (days > 0 ? `${avg.toFixed(1)} / 4` : "—");
  const dirColor = (dir: string) =>
    dir === "improving" ? "#1F6F3D" : dir === "worsening" ? PD_CORAL : PD_SOFT_PLUM;
  // Honesty fix: a row with no prior-30 data has nothing to compare against,
  // so we render "new" instead of the shared helper's default "stable".
  const renderDir = (d: { dir: string; priorDays: number }) =>
    d.priorDays === 0 ? "new" : d.dir;

  const rowsHtml = visibleRows
    .map(d => `<tr>
      <td style="font-weight:500;">${escapeHtml(d.name)}</td>
      <td>${fmtAvg(d.priorAvg, d.priorDays)}</td>
      <td>${fmtAvg(d.lastAvg, d.lastDays)}</td>
      <td>${d.lastDays} / ${d.priorDays}</td>
      <td style="color:${dirColor(renderDir(d))};">${renderDir(d)}</td>
    </tr>`)
    .join("");

  const symptomBlock = rowsHtml
    ? `<table>
        <thead><tr><th>Symptom</th><th>Prior 30 avg</th><th>Last 30 avg</th><th>Days (last / prior)</th><th>Direction</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="note" style="font-size:11px;margin-top:6px;">Not logging a symptom isn't the same as not having it — a gap may just mean it wasn't recorded.</div>`
    : `${emptyNotice("No symptom entries logged in this window.")}
       <div class="note" style="font-size:11px;margin-top:6px;">Not logging a symptom isn't the same as not having it — a gap may just mean it wasn't recorded.</div>`;

  // 90-day heatmap — same data-driven renderer the peri Symptom Intelligence
  // Report uses. Hot flashes (event-tracked) + severity-scored symptoms the
  // patient logged in the last 90 days. Hidden when there's nothing to plot
  // (no logged severity rows AND no hot-flash events).
  const hasAnyHeatmapData = Object.keys(logsByDate || {}).length > 0 || Object.keys(hfCountByDate || {}).length > 0;
  const heatmapBlock = hasAnyHeatmapData
    ? `<div style="margin-top:14px;">
        <h3 style="margin:0 0 6px;font-size:14px;color:${PD_PLUM};">90-day symptom heatmap</h3>
        <div style="font-size:11px;color:${PD_SOFT_PLUM};margin-bottom:6px;">Darker shading = more severe. Empty cells = no log that day.</div>
        ${renderSymptomHeatmapSvg({ logsByDate, hfCountByDate, days })}
      </div>`
    : "";

  // Hot-flash one-liner — graceful zero handling, no clinical sentence.
  const hfBlock = hf.total > 0
    ? `<p style="margin:12px 0 0;font-size:13px;line-height:1.55;">
        <strong>Hot flashes (last 90 days):</strong> ${hf.total} event${hf.total === 1 ? "" : "s"} across ${hf.daysWithEvent} day${hf.daysWithEvent === 1 ? "" : "s"} (median ${hf.medianPerDay}/day).${hf.peakHourBand ? ` Peak window: ${escapeHtml(hf.peakHourBand)}.` : ""}
      </p>`
    : `<p style="margin:12px 0 0;font-size:13px;line-height:1.55;color:${PD_SOFT_PLUM};">
        <strong>Hot flashes (last 90 days):</strong> none logged.
      </p>`;

  return `
    <div class="section">
      <h2>Symptoms</h2>
      <div style="font-size:12px;color:${PD_SOFT_PLUM};margin-bottom:8px;">Last 30 days vs prior 30 days.</div>
      ${symptomBlock}
      ${heatmapBlock}
      ${hfBlock}
    </div>`;
}


// ------------------------------------------------------------
// Trigger Correlations section — sits directly after Symptoms in
// both reports. Renders the FULL high-vs-low comparison set from
// buildTriggerReportData. computeTriggerCorrelations and the
// Insight tab are deliberately not touched.
//
// Three states, all honest:
//   1. No factor has enough data on both sides → graceful single line.
//   2. Factors have data but none clear the strength threshold →
//      show every comparison's numbers, labeled "no clear difference yet."
//   3. One or more clear the threshold → those render as "clear pattern"
//      alongside the weaker (no-clear-difference) ones.
//
// Outcomes shown: (a) mean symptom severity 1–4 — always, when ≥3 days
// per side; (b) hot-flash frequency per day — only when notable.
// Higher = worse for both. Menopause Score is deliberately not the
// head-to-head outcome here.
// ------------------------------------------------------------
function fmtSev(v: number): string { return `${v.toFixed(1)} / 4`; }
function fmtHf(v: number): string { return `${v.toFixed(1)}/day`; }

function triggerRow(c: TriggerComparison): string {
  const fmt = c.outcome === "symptom_severity" ? fmtSev : fmtHf;
  const dir = c.effectSize > 0 ? "↑" : c.effectSize < 0 ? "↓" : "→";
  const verdictColor = c.isPattern ? PD_CORAL : PD_SOFT_PLUM;
  const verdict = c.isPattern ? "clear pattern" : "no clear difference yet";
  const confLabel = c.confidence === "confident" ? "confident"
    : c.confidence === "building" ? "building confidence"
    : "low confidence";
  return `<tr>
    <td style="font-weight:500;">${escapeHtml(c.label)}</td>
    <td>${fmt(c.highMean)} <span style="color:${PD_SOFT_PLUM};font-size:11px;">(${c.highDays}d)</span></td>
    <td>${fmt(c.lowMean)} <span style="color:${PD_SOFT_PLUM};font-size:11px;">(${c.lowDays}d)</span></td>
    <td>${dir} ${Math.abs(c.effectSize).toFixed(1)}</td>
    <td style="color:${verdictColor};font-weight:500;">${verdict}<div style="font-size:10px;color:${PD_SOFT_PLUM};font-weight:400;">${confLabel}</div></td>
  </tr>`;
}

function triggerCorrelationsSection(args: { data: TriggerReportData }): string {
  const { data } = args;
  const win = data.windowDays;
  const methodologyLine = `<div class="note" style="font-size:11px;margin-top:8px;color:${PD_SOFT_PLUM};">Trigger factors are self-reported (caffeine, alcohol) or wearable-derived (stress${data.stressSource === "hrv_proxy" ? " — from HRV" : ""}, sleep, exercise).</div>`;
  const disclaimer = `<div class="note" style="font-size:11px;margin-top:6px;color:${PD_SOFT_PLUM};">These are patterns in your own data, not a diagnosis. Bring them to your doctor.</div>`;
  const intro = `<div style="font-size:12px;color:${PD_SOFT_PLUM};margin-bottom:8px;">Associations in your own logged data over the last ${win} days — not cause-and-effect.</div>`;

  if (!data.hasAnyComparable) {
    return `
    <div class="section">
      <h2>Trigger Patterns</h2>
      ${intro}
      ${emptyNotice("Not enough logged data yet to compare triggers — keep logging and patterns will show here.")}
      ${methodologyLine}
    </div>`;
  }

  const primaryRows = data.primary.map(triggerRow).join("");
  const primaryBlock = primaryRows
    ? `<table>
        <thead><tr>
          <th>Factor</th>
          <th>On high-trigger days</th>
          <th>On low-trigger days</th>
          <th>Difference</th>
          <th>Read</th>
        </tr></thead>
        <tbody>${primaryRows}</tbody>
      </table>
      <div style="font-size:11px;color:${PD_SOFT_PLUM};margin-top:6px;">Outcome: mean symptom severity (1–4, higher = worse).</div>`
    : "";

  const hfRows = data.hotFlashSecondary.map(triggerRow).join("");
  const hfBlock = hfRows
    ? `<p style="margin:14px 0 6px;font-size:12px;color:${PD_PLUM};font-weight:500;">Notable hot-flash patterns</p>
       <table>
         <thead><tr>
           <th>Factor</th>
           <th>On high-trigger days</th>
           <th>On low-trigger days</th>
           <th>Difference</th>
           <th>Read</th>
         </tr></thead>
         <tbody>${hfRows}</tbody>
       </table>
       <div style="font-size:11px;color:${PD_SOFT_PLUM};margin-top:6px;">Outcome: hot-flash events per day (higher = worse). Shown only when notable.</div>`
    : "";

  return `
    <div class="section">
      <h2>Trigger Patterns</h2>
      ${intro}
      ${primaryBlock}
      ${hfBlock}
      ${disclaimer}
      ${methodologyLine}
    </div>`;
}

// ------------------------------------------------------------
// Step 5 — postmeno Mood section (UNWIRED; wiring deferred to Step 6).
//
// Pulls every value from buildMoodSummary (no re-tally). Honors the full
// state machine: pending (<14d) / empty (0 logs) / caveat (<14 logs) /
// ready (≥14 logs) / hidden. fetchMoodsBetween stays — it feeds this.
// Distribution shown as labeled rows (emoji + label + %), not the bar.
// Descriptive only — no diagnosis, no cross-section interpretation.
// ------------------------------------------------------------
function moodSectionLight(args: {
  mood: ReturnType<typeof buildMoodSummary>;
}): string {
  const { mood } = args;

  if (mood.state === "hidden") return "";

  if (mood.state === "pending") {
    const n = mood.daysUntilUnlock ?? 14;
    return `
    <div class="section">
      <h2>Mood</h2>
      ${emptyNotice(`Mood patterns unlock at Day 14 — ${n} day${n === 1 ? "" : "s"} to go.`)}
    </div>`;
  }

  if (mood.state === "empty") {
    return `
    <div class="section">
      <h2>Mood</h2>
      ${emptyNotice("No mood entries logged in the last 90 days.")}
    </div>`;
  }

  // caveat or ready
  const cov = mood.coverage;
  const coverageLine = `<div style="font-size:12px;color:${PD_SOFT_PLUM};margin-bottom:8px;">Logged ${cov.logged} of last 90 days.</div>`;

  const caveatLine = mood.state === "caveat"
    ? `<div class="note" style="font-size:11px;margin-bottom:8px;">Early signal — based on fewer than 14 days of mood entries.</div>`
    : "";

  const mc = mood.mostCommon;
  const mostCommonLine = mc
    ? `<p style="margin:0 0 8px;font-size:13px;line-height:1.55;"><strong>Most common:</strong> ${escapeHtml(mc.label)} (${mc.pct}%)</p>`
    : "";

  const distRows = (mood.distribution || [])
    .map(d => `<tr>
      <td style="font-weight:500;">${escapeHtml(d.label)}</td>
      <td style="text-align:right;">${d.pct}%</td>
    </tr>`).join("");
  const distBlock = distRows
    ? `<table style="width:auto;min-width:220px;">
        <thead><tr><th>Mood</th><th style="text-align:right;">Share</th></tr></thead>
        <tbody>${distRows}</tbody>
      </table>`
    : "";

  let trendBlock = "";
  if (mood.trend) {
    const t = mood.trend;
    const color = t.direction === "improving" ? "#1F6F3D" : t.direction === "declining" ? PD_CORAL : PD_SOFT_PLUM;
    trendBlock = `<p style="margin:8px 0 0;font-size:13px;line-height:1.55;"><strong>Trend:</strong> <span style="color:${color};">${t.direction}</span> across the window.</p>`;
  }

  return `
    <div class="section">
      <h2>Mood</h2>
      ${coverageLine}
      ${caveatLine}
      ${mostCommonLine}
      ${distBlock}
      ${trendBlock}
    </div>`;
}


// ------------------------------------------------------------
// Data Confidence (UNWIRED — not inserted into sectionsHtml).
// Plain-English description of how complete and consistent the
// last-90-days logging has been. Reuses the existing
// buildDataConfidence label + score; no recompute, no rescoring.
// Descriptive only — not a judgment of health, not a clinical claim.
// ------------------------------------------------------------
function dataConfidenceSectionLight(args: { confidence: DataConfidence }): string {
  const { confidence } = args;
  const { label, score, breakdown } = confidence;

  const headline =
    label === "HIGH"
      ? "Your logging has been consistent and thorough."
      : label === "MODERATE"
      ? "Your logging covers enough days to show patterns, with some gaps."
      : "Your logging has been light over the last 90 days.";

  const body =
    label === "HIGH"
      ? "This report reflects a steady, well-rounded picture of the last 90 days. The patterns shown here are based on regular day-to-day entries."
      : label === "MODERATE"
      ? "There's enough day-to-day data here to see meaningful patterns, but a few stretches are missing. Filling in more days going forward will sharpen the picture."
      : "Patterns shown here are early signals based on a small number of logged days. They aren't a full picture of the last 90 days — adding more entries over time will make future reports more useful.";

  const coverageLine = `Logged ${breakdown.loggedDays} of last 90 days · ${breakdown.checkinPct}% check-in rate${breakdown.hrtLogPct != null ? ` · ${breakdown.hrtLogPct}% HRT logged` : ""}.`;

  return `
    <div class="section">
      <h2>Data Confidence</h2>
      <div style="font-size:12px;color:${PD_SOFT_PLUM};margin-bottom:8px;">${escapeHtml(label)} · ${score}/100</div>
      <p style="margin:0 0 8px;font-size:13px;line-height:1.55;"><strong>${escapeHtml(headline)}</strong></p>
      <p style="margin:0 0 8px;font-size:13px;line-height:1.55;">${escapeHtml(body)}</p>
      <div style="font-size:12px;color:${PD_SOFT_PLUM};">${escapeHtml(coverageLine)}</div>
      <p class="note" style="font-size:11px;margin-top:8px;">This describes how complete the logging has been — not a judgment of your health.</p>
    </div>`;
}


// ------------------------------------------------------------
// Discussion Points — "what to bring to your doctor" (UNWIRED).
// Standalone helper, NOT inserted into sectionsHtml. Models the
// peri buildDoctorQuestions structure (candidate pool, each with
// a trigger + priority, sorted by priority, always renders ≥1).
// All triggers reuse already-computed values from other sections —
// no recompute. Plain-English, NON-directive language.
// ------------------------------------------------------------
type DiscussionCandidate = {
  id: string;
  priority: number;
  question: string;
  rationale?: string;
};

function buildPostmenoDiscussionPoints(args: {
  user: any;
  vitality: VitalityScore | null;
  wearableSummary: WearableTodaySummary | null;
  labUploads: LabUploadRow[];
  screenings: HealthScreeningRow[];
  directions: ReturnType<typeof buildSymptomDirections>;
  stage: string;
  onHRT: boolean;
  hrtType: string | undefined;
}): DiscussionCandidate[] {
  const { user, vitality, wearableSummary, labUploads, screenings, directions, stage, onHRT, hrtType } = args;
  const out: DiscussionCandidate[] = [];

  // 1. Fitness for longevity — fitness age ≥5 yrs OLDER than chronological age.
  // deltaYears = age - fitnessAge, so OLDER fitness age ⇒ deltaYears ≤ -5.
  const vo2 = wearableSummary?.vo2Max?.value ?? null;
  const age = ageFromDob(user?.dob);
  const fa = vo2 != null && age != null ? fitnessAgeFromVO2max(vo2, age) : null;
  if (fa && fa.deltaYears <= -5) {
    out.push({
      id: "fitness-longevity",
      priority: 2,
      question: `My fitness age is reading about ${Math.abs(fa.deltaYears)} years older than my chronological age. Worth discussing what I can do — cardio, strength, recovery — to support long-term heart and lung health.`,
      rationale: `fitnessAge ${fa.fitnessAge} vs age ${age} (delta ${fa.deltaYears})`,
    });
  }

  // 2. Recovery — vitality band is the lowest band (id === "difficult").
  if (vitality?.band?.id === "difficult") {
    out.push({
      id: "recovery-low",
      priority: 1,
      question: `My recovery score has been sitting in the lowest band lately. Consider asking your doctor whether sleep, stress, or another underlying factor is worth looking into.`,
      rationale: `vitality.band.id = "difficult" (score ${vitality.score ?? "—"})`,
    });
  }

  // 3. Out-of-range labs — latest value per marker outside its reference range.
  const grouped = groupByMarker(labUploads || []);
  const outOfRange: string[] = [];
  for (const m of LAB_MARKERS) {
    const pts = grouped[m.key];
    if (!pts || pts.length === 0) continue;
    const last = pts[pts.length - 1];
    if (last == null || typeof last.value !== "number") continue;
    const range = getRange(m.key as MarkerKey, stage as LifeStage, onHRT, hrtType);
    const tooLow = range.low != null && last.value < range.low;
    const tooHigh = range.high != null && last.value > range.high;
    if (tooLow || tooHigh) outOfRange.push(m.displayName);
  }
  if (outOfRange.length > 0) {
    const list = outOfRange.length === 1
      ? outOfRange[0]
      : outOfRange.length === 2
        ? `${outOfRange[0]} and ${outOfRange[1]}`
        : `${outOfRange.slice(0, -1).join(", ")}, and ${outOfRange[outOfRange.length - 1]}`;
    out.push({
      id: "labs-out-of-range",
      priority: 1,
      question: `My most recent ${list} ${outOfRange.length === 1 ? "result is" : "results are"} outside the typical reference range for my stage. Worth asking what — if anything — that means in my context, and whether a recheck is warranted.`,
      rationale: `out-of-range markers: ${outOfRange.join(", ")}`,
    });
  }

  // 4. Overdue screenings — statusFor(row) === "overdue".
  const overdue = (screenings || [])
    .filter(r => statusFor(r) === "overdue")
    .map(r => SCREENING_LABEL[r.screening_type] || r.screening_type);
  if (overdue.length > 0) {
    const list = overdue.length === 1
      ? overdue[0]
      : overdue.length === 2
        ? `${overdue[0]} and ${overdue[1]}`
        : `${overdue.slice(0, -1).join(", ")}, and ${overdue[overdue.length - 1]}`;
    out.push({
      id: "screenings-overdue",
      priority: 1,
      question: `${list} ${overdue.length === 1 ? "is" : "are"} past the next-due date in my records. Worth scheduling, or confirming the timing if I've already had it done.`,
      rationale: `overdue: ${overdue.join(", ")}`,
    });
  }

  // 5. Ongoing symptoms — any symptom direction = "worsening", or ≥3 still active.
  const dirs = directions?.directions || [];
  const worsening = dirs.filter(d => d.dir === "worsening").map(d => d.name);
  const activeCount = dirs.filter(d => (d.lastDays || 0) > 0).length;
  if (worsening.length > 0) {
    const list = worsening.length === 1
      ? worsening[0]
      : `${worsening.slice(0, -1).join(", ")}${worsening.length > 2 ? "," : ""} and ${worsening[worsening.length - 1]}`;
    out.push({
      id: "symptoms-worsening",
      priority: 2,
      question: `${list} ${worsening.length === 1 ? "has" : "have"} been trending worse over the last 30 days versus the prior 30. Worth raising so we can talk through what might be driving the change.`,
      rationale: `worsening directions: ${worsening.join(", ")}`,
    });
  } else if (activeCount >= 3) {
    out.push({
      id: "symptoms-ongoing",
      priority: 3,
      question: `I'm still logging ${activeCount} different symptoms regularly. Even in postmenopause, it's worth checking in on what's persisting and whether anything new is worth investigating.`,
      rationale: `${activeCount} symptoms with lastDays > 0`,
    });
  }

  // 6. HRT — branch on onHRT. (Test account is hrt=None → "off-HRT" branch fires.)
  if (onHRT) {
    // UNTESTED on this account — test account has hrt=None.
    out.push({
      id: "hrt-on",
      priority: 2,
      question: `I've been on ${hrtType || "my current HRT"} — worth checking in on how it's working overall, whether the dose still feels right, and how my adherence has been.`,
      rationale: "onHRT === true (UNTESTED on this account)",
    });
  } else {
    out.push({
      id: "hrt-off",
      priority: 4,
      question: `I'm not currently on hormone therapy. Depending on my symptoms and risk profile, it may be worth a conversation about whether HRT — or non-hormonal options — would make sense for me.`,
      rationale: "onHRT === false",
    });
  }

  // 7. Default fallback — always present so section is never empty.
  out.push({
    id: "default-checkin",
    priority: 9,
    question: `Anything in this report — labs, screenings, recovery, symptoms — that stands out to you that I should be paying more attention to?`,
    rationale: "always included",
  });

  return out.sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
}

function discussionPointsSectionLight(args: {
  user: any;
  vitality: VitalityScore | null;
  wearableSummary: WearableTodaySummary | null;
  labUploads: LabUploadRow[];
  screenings: HealthScreeningRow[];
  directions: ReturnType<typeof buildSymptomDirections>;
  stage: string;
  onHRT: boolean;
  hrtType: string | undefined;
  triggerReport: TriggerReportData | null;
}): string {
  const points = buildPostmenoDiscussionPoints(args);
  const triggerLines = args.triggerReport ? buildTriggerDiscussionPoints(args.triggerReport) : [];
  const triggerItems = triggerLines.map(line =>
    `<li style="margin:0 0 10px;font-size:13px;line-height:1.55;">${escapeHtml(line)}</li>`
  ).join("");
  const items = points.map(p =>
    `<li style="margin:0 0 10px;font-size:13px;line-height:1.55;">${escapeHtml(p.question)}</li>`
  ).join("");
  return `
    <div class="section">
      <h2>Discussion Points</h2>
      <p style="margin:0 0 10px;font-size:12px;color:${PD_SOFT_PLUM};">A few things worth bringing up at your next visit, drawn from this report.</p>
      <ul style="margin:0;padding-left:20px;">${items}${triggerItems}</ul>
      <p class="note" style="font-size:11px;margin-top:10px;">These are conversation prompts, not medical advice. Bring whatever is helpful and skip what isn't.</p>
    </div>`;
}


// ------------------------------------------------------------
// Clinician Summary (UNWIRED — not inserted into sectionsHtml).
// A short factual recap for a clinician reading the report.
// Pulls from values already computed for other sections — recovery
// band, fitness age vs chronological age, out-of-range labs,
// overdue screenings, symptom directions, HRT status, data
// confidence label. No recompute. Descriptive only — no diagnosis,
// no recommendations, no "clinical decisions" framing.
// ------------------------------------------------------------
function clinicianSummarySectionLight(args: {
  user: any;
  vitality: VitalityScore | null;
  wearableSummary: WearableTodaySummary | null;
  labUploads: LabUploadRow[];
  screenings: HealthScreeningRow[];
  directions: ReturnType<typeof buildSymptomDirections>;
  confidence: DataConfidence;
  stage: string;
  onHRT: boolean;
  hrtType: string | undefined;
}): string {
  const { user, vitality, wearableSummary, labUploads, screenings, directions, confidence, stage, onHRT, hrtType } = args;

  const lines: string[] = [];

  // Recovery band
  if (vitality?.band && typeof vitality.score === "number") {
    lines.push(`Recovery (Vitality Score) sits in the ${vitality.band.label.toLowerCase()} band over the last 14 days (avg ${vitality.score}/100).`);
  } else {
    lines.push(`Recovery (Vitality Score): not available — insufficient wearable baseline.`);
  }

  // Fitness age vs chronological age
  const vo2 = wearableSummary?.vo2Max?.value ?? null;
  const age = ageFromDob(user?.dob);
  const fa = vo2 != null && age != null ? fitnessAgeFromVO2max(vo2, age) : null;
  if (fa && age != null) {
    const delta = fa.deltaYears;
    const phrase = delta === 0
      ? `equal to chronological age (${age})`
      : delta > 0
        ? `${delta} year${delta === 1 ? "" : "s"} younger than chronological age (${age})`
        : `${Math.abs(delta)} year${Math.abs(delta) === 1 ? "" : "s"} older than chronological age (${age})`;
    lines.push(`Fitness age ${fa.fitnessAge} — ${phrase}.`);
  } else {
    lines.push(`Fitness age: not available${age != null ? ` (chronological age ${age})` : ""}.`);
  }

  // Out-of-range labs
  const grouped = groupByMarker(labUploads || []);
  const outOfRange: string[] = [];
  for (const m of LAB_MARKERS) {
    const pts = grouped[m.key];
    if (!pts || pts.length === 0) continue;
    const last = pts[pts.length - 1];
    if (last == null || typeof last.value !== "number") continue;
    const range = getRange(m.key as MarkerKey, stage as LifeStage, onHRT, hrtType);
    const tooLow = range.low != null && last.value < range.low;
    const tooHigh = range.high != null && last.value > range.high;
    if (tooLow || tooHigh) outOfRange.push(m.displayName);
  }
  if ((labUploads || []).length === 0) {
    lines.push(`Labs: no uploads on file.`);
  } else if (outOfRange.length === 0) {
    lines.push(`Labs: latest values within reference range for stage.`);
  } else {
    lines.push(`Labs outside reference range (latest value): ${outOfRange.join(", ")}.`);
  }

  // Overdue screenings
  const overdue = (screenings || [])
    .filter(r => statusFor(r) === "overdue")
    .map(r => SCREENING_LABEL[r.screening_type] || r.screening_type);
  if ((screenings || []).length === 0) {
    lines.push(`Screenings: none on file.`);
  } else if (overdue.length === 0) {
    lines.push(`Screenings: none overdue per recorded next-due dates.`);
  } else {
    lines.push(`Overdue screenings: ${overdue.join(", ")}.`);
  }

  // Symptom directions over 60-day window
  const dirs = directions?.directions || [];
  const worsening = dirs.filter(d => d.dir === "worsening").map(d => d.name);
  const improving = dirs.filter(d => d.dir === "improving").map(d => d.name);
  const activeCount = dirs.filter(d => (d.lastDays || 0) > 0).length;
  if (dirs.length === 0) {
    lines.push(`Symptom directions: no logged symptoms in the 60-day window.`);
  } else {
    // Honesty fix: if no symptom has any prior-30 data, there's nothing to
    // compare against — don't claim "stable."
    const anyPrior = dirs.some(d => (d.priorDays || 0) > 0);
    const parts: string[] = [`${activeCount} active over last 30 days`];
    if (worsening.length) parts.push(`worsening: ${worsening.join(", ")}`);
    if (improving.length) parts.push(`improving: ${improving.join(", ")}`);
    if (!worsening.length && !improving.length) {
      parts.push(anyPrior
        ? `all stable vs prior 30 days`
        : `first logged this period — no prior-30 data to compare`);
    }
    lines.push(`Symptoms — ${parts.join("; ")}.`);
  }

  // HRT status
  if (onHRT) {
    lines.push(`HRT: currently on ${hrtType || "hormone therapy"}.`);
  } else {
    lines.push(`HRT: not currently on hormone therapy.`);
  }

  // Data confidence
  lines.push(`Data confidence: ${confidence.label} (${confidence.score}/100, ${confidence.breakdown.loggedDays}/90 days logged).`);

  const items = lines.map(l =>
    `<li style="margin:0 0 6px;font-size:13px;line-height:1.55;">${escapeHtml(l)}</li>`
  ).join("");

  return `
    <div class="section">
      <h2>Clinician Summary</h2>
      <p style="margin:0 0 10px;font-size:12px;color:${PD_SOFT_PLUM};">A factual recap of what this report contains, drawn from the sections above.</p>
      <ul style="margin:0;padding-left:20px;list-style:disc;">${items}</ul>
      <p class="note" style="font-size:11px;margin-top:10px;">Descriptive summary only — not a diagnosis or treatment recommendation.</p>
    </div>`;
}


// ------------------------------------------------------------
// Your Summary (UNWIRED — not inserted into sectionsHtml).
// One plain-English sentence to anchor the top of the report.
// Frames what the report covers and the overall data confidence
// in warm, everyday language. No overclaiming.
// ------------------------------------------------------------
function yourSummarySectionLight(args: { confidence: DataConfidence }): string {
  const { confidence } = args;
  const { label, breakdown } = confidence;
  const days = breakdown.loggedDays;

  const sentence =
    label === "HIGH"
      ? `Here's an overview of your last 90 days in your Vitality Report — recovery, labs, screenings, symptoms, and hormone therapy — built from ${days} days of consistent logging, so the picture is a confident one.`
      : label === "MODERATE"
      ? `Here's an overview of your last 90 days in your Vitality Report — recovery, labs, screenings, symptoms, and hormone therapy — built from ${days} days of logging, enough to see real patterns with a few gaps.`
      : `Here's an overview of your last 90 days in your Vitality Report — recovery, labs, screenings, symptoms, and hormone therapy — built from ${days} days of logging, so treat what's here as early signals rather than a full picture.`;

  return `
    <div class="section">
      <h2>Your Summary</h2>
      <p style="margin:0;font-size:14px;line-height:1.6;">${escapeHtml(sentence)}</p>
    </div>`;
}


// ------------------------------------------------------------
// Your Results — In Plain English (UNWIRED — not in sectionsHtml).
// Centerpiece user-facing recap of the Vitality Report. Reuses
// the SAME computed inputs as Discussion Points / Clinician Summary
// so the three can never disagree. Each block is gated on having
// data; missing data is skipped, never fabricated. No diagnosis,
// no prescriptions, no outcome promises, no "clinical" wording.
// ------------------------------------------------------------
function plainEnglishResultsSectionLight(args: {
  user: any;
  vitality: VitalityScore | null;
  wearableSummary: WearableTodaySummary | null;
  labUploads: LabUploadRow[];
  screenings: HealthScreeningRow[];
  directions: ReturnType<typeof buildSymptomDirections>;
  mood: ReturnType<typeof buildMoodSummary> | null;
  confidence: DataConfidence;
  stage: string;
  onHRT: boolean;
  hrtType: string | undefined;
}): string {
  const { user, vitality, wearableSummary, labUploads, screenings, directions, mood, confidence, stage, onHRT, hrtType } = args;

  // ---- Derive the same buckets the Discussion Points use ----
  const vo2 = wearableSummary?.vo2Max?.value ?? null;
  const age = ageFromDob(user?.dob);
  const fa = vo2 != null && age != null ? fitnessAgeFromVO2max(vo2, age) : null;

  const grouped = groupByMarker(labUploads || []);
  const labsOutOfRange: string[] = [];
  const labsInRange: string[] = [];
  for (const m of LAB_MARKERS) {
    const pts = grouped[m.key];
    if (!pts || pts.length === 0) continue;
    const last = pts[pts.length - 1];
    if (last == null || typeof last.value !== "number") continue;
    const range = getRange(m.key as MarkerKey, stage as LifeStage, onHRT, hrtType);
    const tooLow = range.low != null && last.value < range.low;
    const tooHigh = range.high != null && last.value > range.high;
    if (tooLow || tooHigh) labsOutOfRange.push(m.displayName);
    else labsInRange.push(m.displayName);
  }
  const hasLabs = (labUploads || []).length > 0 && (labsInRange.length + labsOutOfRange.length) > 0;

  const overdue = (screenings || [])
    .filter(r => statusFor(r) === "overdue")
    .map(r => SCREENING_LABEL[r.screening_type] || r.screening_type);
  const hasScreenings = (screenings || []).length > 0;

  const dirs = directions?.directions || [];
  const worsening = dirs.filter(d => d.dir === "worsening").map(d => d.name);
  const improving = dirs.filter(d => d.dir === "improving").map(d => d.name);
  const stableActive = dirs.filter(d => d.dir === "stable" && (d.lastDays || 0) > 0).map(d => d.name);
  const activeCount = dirs.filter(d => (d.lastDays || 0) > 0).length;
  const hasSymptomData = dirs.length > 0;

  const recoveryOk = vitality?.status === "ok" && vitality.band != null;
  const recoveryHealthy = recoveryOk && vitality.band!.id !== "difficult";
  const recoveryLow = recoveryOk && vitality.band!.id === "difficult";

  const fitnessHealthy = fa != null && fa.deltaYears >= 0;        // at or younger
  const fitnessOlder = fa != null && fa.deltaYears <= -5;          // ≥5 yrs older — same trigger as DP

  // ---- 1. Big Picture ----
  const confLine =
    confidence.label === "HIGH"
      ? `Because you've logged ${confidence.breakdown.loggedDays} of the last 90 days, what you're seeing here is a confident picture.`
      : confidence.label === "MODERATE"
      ? `You've logged ${confidence.breakdown.loggedDays} of the last 90 days — enough to see real patterns, with a few gaps to fill in over time.`
      : `You've logged ${confidence.breakdown.loggedDays} of the last 90 days, so treat what's here as early signals rather than a full picture — more entries will sharpen the next report.`;
  const bigPicture = `<p style="margin:0 0 8px;font-size:13px;line-height:1.6;">This report is about the through-line that matters most: <strong>what helps you live longer and feel better</strong> — your recovery, your fitness, what your labs and screenings say, and how your symptoms are trending.</p>
    <p style="margin:0 0 8px;font-size:13px;line-height:1.6;">${escapeHtml(confLine)}</p>`;

  // ---- 2. What's Working ----
  const working: string[] = [];
  if (recoveryHealthy) working.push(`Your recovery has been sitting in a healthy range overall.`);
  if (fitnessHealthy) working.push(`Your fitness age is at or younger than your actual age — a good sign for long-term heart and lung health.`);
  if (hasLabs && labsOutOfRange.length === 0) working.push(`Your most recent lab values are inside the typical range for your stage.`);
  if (hasScreenings && overdue.length === 0) working.push(`Your screenings are current based on the next-due dates on file.`);
  if (improving.length > 0) working.push(`These symptoms have been trending better over the last 30 days versus the prior 30: ${improving.join(", ")}.`);
  // Only call symptoms "held steady" when there's an actual prior period to compare to.
  const anyPrior = dirs.some(d => (d.priorDays || 0) > 0);
  if (hasSymptomData && anyPrior && worsening.length === 0 && improving.length === 0 && stableActive.length > 0) {
    working.push(`Your day-to-day symptoms have held steady over the last 30 days — no clear worsening trend.`);
  }

  // ---- 3. Keep an Eye On ----
  const watch: string[] = [];
  if (recoveryLow) watch.push(`Your recovery has been sitting in the lowest band lately. Worth bringing up with your doctor — sleep, stress, or another underlying factor may be worth a look.`);
  if (fitnessOlder && fa) watch.push(`Your fitness age is reading about ${Math.abs(fa.deltaYears)} years older than your actual age. Worth discussing what could support your heart and lung fitness over time.`);
  if (hasLabs && labsOutOfRange.length > 0) watch.push(`Your most recent ${labsOutOfRange.join(", ")} ${labsOutOfRange.length === 1 ? "result is" : "results are"} outside the typical range for your stage. Worth asking your doctor what — if anything — that means for you.`);
  if (hasScreenings && overdue.length > 0) watch.push(`These screenings are past the next-due date on file: ${overdue.join(", ")}. Worth scheduling, or confirming the timing if you've already had ${overdue.length === 1 ? "it" : "them"} done.`);
  if (worsening.length > 0) watch.push(`These symptoms have been trending worse over the last 30 days versus the prior 30: ${worsening.join(", ")}. Worth raising so you and your doctor can talk through what might be driving the change.`);

  const workingHtml = working.length === 0
    ? `<p style="margin:0 0 6px;font-size:13px;line-height:1.6;color:${PD_SOFT_PLUM};">Not enough data yet to call out specific bright spots — keep logging and the next report will fill this in.</p>`
    : `<ul style="margin:0 0 8px;padding-left:20px;">${working.map(s => `<li style="margin:0 0 6px;font-size:13px;line-height:1.6;">${escapeHtml(s)}</li>`).join("")}</ul>`;

  const watchHtml = watch.length === 0
    ? `<p style="margin:0 0 6px;font-size:13px;line-height:1.6;color:${PD_SOFT_PLUM};">Nothing stands out as off in the data you've logged so far.</p>`
    : `<ul style="margin:0 0 8px;padding-left:20px;">${watch.map(s => `<li style="margin:0 0 6px;font-size:13px;line-height:1.6;">${escapeHtml(s)}</li>`).join("")}</ul>`;

  // ---- 4. Per-area plain reads ----
  const areas: { title: string; body: string }[] = [];

  if (recoveryOk) {
    const bandLabel = vitality.band!.label.toLowerCase();
    const score = vitality.score;
    const recoveryBody = recoveryLow
      ? `Recovery measures how rested your body looks overnight — sleep, heart-rate variability, and resting heart rate compared to your own baseline. Lately yours has been in the lowest band (around ${score}/100). That doesn't tell you why, but it's worth flagging.`
      : `Recovery measures how rested your body looks overnight — sleep, heart-rate variability, and resting heart rate compared to your own baseline. Lately yours has been in the ${bandLabel} band (around ${score}/100), which is a good sign your body is bouncing back day to day.`;
    areas.push({ title: "Recovery", body: recoveryBody });
  }

  if (fa != null && age != null) {
    const delta = fa.deltaYears;
    const fitBody = delta >= 0
      ? `Your fitness age is an estimate from your VO2max — how efficiently your body uses oxygen. Yours is reading ${fa.fitnessAge}, which is at or younger than your actual age (${age}). Cardio fitness is one of the strongest predictors of long-term heart and lung health, so this is a good signal.`
      : delta > -5
        ? `Your fitness age is an estimate from your VO2max — how efficiently your body uses oxygen. Yours is reading ${fa.fitnessAge}, about ${Math.abs(delta)} year${Math.abs(delta) === 1 ? "" : "s"} older than your actual age (${age}). It's a small gap, and cardio fitness is something you can move with consistent activity over time.`
        : `Your fitness age is an estimate from your VO2max — how efficiently your body uses oxygen. Yours is reading ${fa.fitnessAge}, about ${Math.abs(delta)} year${Math.abs(delta) === 1 ? "" : "s"} older than your actual age (${age}). Worth talking with your doctor about cardio, strength, and recovery as ways to support long-term heart and lung health.`;
    areas.push({ title: "Fitness age", body: fitBody });
  }

  if (hasLabs) {
    const labsBody = labsOutOfRange.length === 0
      ? `Your latest lab values are inside the typical reference range for your stage. The reference ranges are general guides — your doctor knows what's normal for you.`
      : `Your most recent ${labsOutOfRange.join(", ")} ${labsOutOfRange.length === 1 ? "value is" : "values are"} outside the typical reference range for your stage. That doesn't mean something is wrong — ranges are general guides, and a value can be off for many reasons. Worth discussing with your doctor what it means in your context.`;
    areas.push({ title: "Labs", body: labsBody });
  }

  if (hasScreenings) {
    const screeningsBody = overdue.length === 0
      ? `Based on the next-due dates on file, your screenings are current. Staying current on age-appropriate screenings is one of the most reliable things you can do for long-term health.`
      : `Based on the next-due dates on file, ${overdue.join(", ")} ${overdue.length === 1 ? "is" : "are"} overdue. Worth scheduling, or letting your doctor know if you've already had ${overdue.length === 1 ? "it" : "them"} done so the record can be updated.`;
    areas.push({ title: "Screenings", body: screeningsBody });
  }

  if (hasSymptomData) {
    const parts: string[] = [];
    parts.push(`You logged ${activeCount} symptom${activeCount === 1 ? "" : "s"} regularly over the last 30 days.`);
    if (worsening.length) parts.push(`Trending worse: ${worsening.join(", ")}.`);
    if (improving.length) parts.push(`Trending better: ${improving.join(", ")}.`);
    if (!worsening.length && !improving.length) {
      parts.push(anyPrior
        ? `Most have held steady compared to the prior 30 days.`
        : `This is your first stretch of symptom logging, so there's no prior period to compare against yet.`);
    }
    parts.push(`Symptom directions compare your last 30 days to the 30 before that — they're a snapshot of change, not a verdict on what's causing anything.`);
    areas.push({ title: "Symptoms", body: parts.join(" ") });
  }

  const moodLogged = mood?.coverage?.logged ?? 0;
  if (mood && (mood.state === "ready" || mood.state === "caveat") && moodLogged > 0) {
    const moodBody = `You logged your mood on ${moodLogged} day${moodLogged === 1 ? "" : "s"} recently. Mood is part of the bigger picture — energy, sleep, and recovery often move together — and it's worth flagging to your doctor if something feels persistently off.`;
    areas.push({ title: "Mood", body: moodBody });
  }



  // HRT — always say something so the user knows where they stand.
  const hrtBody = onHRT
    ? `You're currently on ${hrtType || "hormone therapy"}. Worth checking in with your doctor on how it's working for you overall, whether the dose still feels right, and how your adherence has been.`
    : `You're not currently on hormone therapy. Depending on your symptoms and your personal risk profile, it may be worth a conversation with your doctor about whether HRT — or non-hormonal options — would make sense for you.`;
  areas.push({ title: "Hormone therapy", body: hrtBody });

  const areasHtml = areas.map(a => `
    <div style="margin:0 0 10px;">
      <div style="font-size:13px;font-weight:600;margin-bottom:3px;">${escapeHtml(a.title)}</div>
      <p style="margin:0;font-size:13px;line-height:1.6;">${escapeHtml(a.body)}</p>
    </div>`).join("");

  // ---- 5. What to Focus On ----
  const focus: string[] = [];
  focus.push(`Cardio and strength: regular movement is one of the most evidence-backed ways to support long-term heart, lung, bone, and brain health. Worth discussing with your doctor what kind and how much fits your life right now.`);
  focus.push(`Sleep and recovery: protecting sleep — and giving your body lighter days when recovery is low — is a lever you can actually pull. Worth talking through if sleep has been an issue.`);
  focus.push(`Staying current on screenings: catching things early is one of the highest-leverage things you can do. Worth flagging anything overdue at your next visit.`);
  if (worsening.length > 0 || (hasLabs && labsOutOfRange.length > 0) || recoveryLow) {
    focus.push(`Bringing the items in "What to keep an eye on" to your next appointment so you and your doctor can decide together what — if anything — needs follow-up.`);
  }
  const focusHtml = `<ul style="margin:0;padding-left:20px;">${focus.map(s => `<li style="margin:0 0 6px;font-size:13px;line-height:1.6;">${escapeHtml(s)}</li>`).join("")}</ul>`;

  return `
    <div class="section">
      <h2>Your Results — In Plain English</h2>

      <h3 style="font-size:13px;margin:8px 0 6px;">The big picture</h3>
      ${bigPicture}

      <h3 style="font-size:13px;margin:14px 0 6px;">What's working</h3>
      ${workingHtml}

      <h3 style="font-size:13px;margin:14px 0 6px;">What to keep an eye on</h3>
      ${watchHtml}

      <h3 style="font-size:13px;margin:14px 0 6px;">A closer look, area by area</h3>
      ${areasHtml}

      <h3 style="font-size:13px;margin:14px 0 6px;">What to focus on next</h3>
      <p style="margin:0 0 8px;font-size:13px;line-height:1.6;">These are evidence-based levers worth discussing with your doctor — not prescriptions, and not promises about outcomes.</p>
      ${focusHtml}

      <p class="note" style="font-size:11px;margin-top:12px;">This is a plain-English read of your own data, not medical advice. Bring it to your doctor and use it as a starting point for the conversation.</p>
    </div>`;
}















// ============================================================
// Section 7 — Doctor Discussion Prompts (smart, optional)
// ============================================================
function discussionSection(args: {
  screenings: HealthScreeningRow[];
  labUploads: LabUploadRow[];
  stage: string;
  onHRT: boolean;
  hrtType: string | undefined;
  mood: ReturnType<typeof buildMoodSummary>;
  milestoneDay: number;
  vitalityScored: number;
  vitalityAvg: number | null;
}): string {
  const { screenings, labUploads, onHRT, stage, hrtType, mood, milestoneDay, vitalityScored, vitalityAvg } = args;

  const screeningStatus = (key: string): ScreeningStatus => {
    const row = screenings.find(s => s.screening_type === key);
    return statusFor(row);
  };
  const grouped = groupByMarker(labUploads || []);
  const labTrend = (k: MarkerKey): ReturnType<typeof computeTrend> => {
    const pts = grouped[k];
    if (!pts || pts.length < 2) return null;
    return computeTrend(pts.slice(-3).map(p => p.value));
  };
  const monthsTracked = Math.floor(milestoneDay / 30);

  const RULES: { when: () => boolean; prompt: string }[] = [
    { when: () => screeningStatus("mammogram") === "overdue", prompt: "Ask about scheduling your mammogram — it's currently overdue." },
    { when: () => screeningStatus("dexa") === "overdue", prompt: "Bone density (DEXA) is overdue — important for postmenopause bone health." },
    { when: () => screeningStatus("colonoscopy") === "overdue", prompt: "Colonoscopy is overdue — discuss timing at your visit." },
    { when: () => screeningStatus("lipid_panel") === "overdue", prompt: "Lipid panel is overdue — cardiovascular risk shifts in postmenopause." },
    { when: () => screeningStatus("a1c") === "overdue", prompt: "A1C / blood sugar screening is overdue — worth catching up on." },
    { when: () => labTrend("estradiol") === "down" && !onHRT, prompt: "Discuss bone-density and cardiovascular implications of falling estradiol." },
    { when: () => labTrend("tsh") === "up" || labTrend("tsh") === "down", prompt: "TSH is trending — ask whether thyroid follow-up is needed." },
    { when: () => mood.state === "ready" && mood.mostCommon != null && (mood.mostCommon.mood >= 3) && monthsTracked >= 3, prompt: "Discuss persistent low mood patterns with your provider." },
    { when: () => vitalityScored >= 30 && vitalityAvg != null && vitalityAvg < 50, prompt: "Your 12-month Vitality average is below 50 — bring this PDF to discuss symptom burden." },
    { when: () => onHRT && (!hrtType || hrtType === "Not sure"), prompt: "Confirm your exact HRT type and dose with your provider so it can be tracked accurately." },
  ];
  const prompts = RULES.filter(r => {
    try { return r.when(); } catch { return false; }
  }).map(r => r.prompt);
  if (prompts.length === 0) return "";
  const items = prompts.map(p => `<li style="margin-bottom:6px;">${escapeHtml(p)}</li>`).join("");
  return `
    <div class="section">
      <h2>Things to Discuss at Your Next Visit</h2>
      <ul style="font-size:13px;line-height:1.6;padding-left:20px;margin:8px 0;">${items}</ul>
    </div>`;
}

// ============================================================
// Main entry point
// ============================================================
export async function generateAnnualWellnessSummaryPDF(
  user: any,
  opts: { labUploads?: LabUploadRow[]; screenings?: HealthScreeningRow[] } = {},
): Promise<void> {
  // Pre-open the tab SYNCHRONOUSLY so the popup-blocker gesture
  // exemption survives the awaits below. If null (popup blocked /
  // in-app browser), finalizeReportWindow shows an in-app fallback
  // link instead of silently failing.
  const w = prepareReportWindow();

  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const name = user ? (`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.name || "Patient") : "Patient";
  const dob = user?.dob || "Not on file";
  const stage = user?.stage || user?.lifeStage || "Postmenopause";
  const onHRT = isOnHrt(user);
  const hrtType: string | undefined = onHRT ? (getHrtName(user) || undefined) : undefined;

  // Resolve userId (auth) so we can run the supabase-backed fetches even if
  // the caller didn't pass user.id explicitly.
  const { data: authData } = await supabase.auth.getUser();
  const userId = user?.id || authData?.user?.id;

  // ---- Parallel fetches ----
  const last90Start = (() => {
    const d = new Date(); d.setDate(d.getDate() - 89);
    return localDateKey(d);
  })();
  const todayKey = localDateKey();

  const [
    scoreSeries,
    reportData,
    hfEvents,
    moodRows,
    hrtMap,
    screeningsFetched,
    labsFetched,
    vitalityScore,
    vitalitySeries,
    wearableRows,
    triggerReport,
    stressSummary,
  ] = await Promise.all([
    userId ? fetchMenoScoreHistory(userId, 365).catch(e => { console.warn("[AWS] score history failed", e); return [] as DailyScore[]; }) : Promise.resolve([] as DailyScore[]),
    fetchReportData(user).catch(e => { console.warn("[AWS] reportData failed", e); return null as any; }),
    userId ? fetchHotFlashEventsBetween(userId, last90Start, todayKey).catch(() => []) : Promise.resolve([]),
    userId ? fetchMoodsBetween(userId, last90Start, todayKey).catch(() => []) : Promise.resolve([]),
    userId && onHRT ? fetchHrtBetween(userId, last90Start, todayKey).catch(() => ({} as Record<string, boolean>)) : Promise.resolve({} as Record<string, boolean>),
    opts.screenings ? Promise.resolve(opts.screenings) : Promise.resolve([] as HealthScreeningRow[]),
    opts.labUploads ? Promise.resolve(opts.labUploads) : Promise.resolve([] as LabUploadRow[]),
    loadVitalityScoreForCurrentUser().catch(e => { console.warn("[AWS] vitality score failed", e); return null as VitalityScore | null; }),
    loadVitalityScoreSeriesForCurrentUser(14).catch(e => { console.warn("[AWS] vitality series failed", e); return [] as VitalityScoreSeriesPoint[]; }),
    userId ? fetchWearableMetrics(userId, 60).catch(e => { console.warn("[AWS] wearable metrics failed", e); return [] as any[]; }) : Promise.resolve([] as any[]),
    userId
      ? buildTriggerReportData(userId, 30).catch(e => { console.warn("[AWS] trigger report failed", e); return { windowDays: 30, hasAnyComparable: false, primary: [], hotFlashSecondary: [], stressSource: null } as TriggerReportData; })
      : Promise.resolve({ windowDays: 30, hasAnyComparable: false, primary: [], hotFlashSecondary: [], stressSource: null } as TriggerReportData),
    userId ? fetchWearableStress(userId, 30).catch(e => { console.warn("[AWS] wearable stress failed", e); return { latest: { value: null, date: null }, n: 0, avg: null, min: null, max: null, direction: "insufficient", absDelta: null } as any; }) : Promise.resolve({ latest: { value: null, date: null }, n: 0, avg: null, min: null, max: null, direction: "insufficient", absDelta: null } as any),
  ]);
  const wearableSummary: WearableTodaySummary = summarizeForToday(wearableRows as any);

  // ---- Derive section inputs ----
  // Derive the set of symptom names actually logged across the 60-day window
  // (last-30 ∪ prior-30) so the postmeno report shows every symptom the user
  // logged — not just REPORT_SYMPTOMS. Excludes EVENT_SYMPTOMS (hot flashes),
  // which have their own block. Sort most-active first (lastDays + priorDays
  // desc, alphabetical tiebreak). Same single source of direction math —
  // buildSymptomDirections is parameterized, not cloned.
  let directions: ReturnType<typeof buildSymptomDirections>;
  if (reportData) {
    const eventSet = new Set<string>(EVENT_SYMPTOMS as readonly string[]);
    const last60Keys: string[] = [];
    for (let i = 0; i < 60; i++) {
      const d = new Date(); d.setDate(d.getDate() - i);
      last60Keys.push(localDateKey(d));
    }
    const nameSet = new Set<string>();
    for (const k of last60Keys) {
      const row = reportData.logsByDate[k];
      if (!row) continue;
      for (const n of Object.keys(row)) {
        if (!eventSet.has(n)) nameSet.add(n);
      }
    }
    const allNames = Array.from(nameSet);
    const preliminary = buildSymptomDirections(reportData, allNames);
    const sorted = [...preliminary.directions].sort((a, b) => {
      const aActivity = a.lastDays + a.priorDays;
      const bActivity = b.lastDays + b.priorDays;
      if (bActivity !== aActivity) return bActivity - aActivity;
      return a.name.localeCompare(b.name);
    });
    directions = { ...preliminary, directions: sorted };
  } else {
    directions = { directions: [], loggedLast30: 0, loggedPrior30: 0 };
  }

  const milestoneDay = reportData?.milestoneDay ?? 1;
  const mood = buildMoodSummary({ milestoneDay, moods: moodRows as any });
  const hfSummary = summarizeHotFlashEvents(hfEvents as any);
  // Per-day hot flash event counts for the heatmap Hot flashes row.
  const hfCountByDate: Record<string, number> = {};
  for (const ev of (hfEvents as any[]) || []) {
    const dk = localDateKey(new Date(ev.occurred_at));
    hfCountByDate[dk] = (hfCountByDate[dk] || 0) + 1;
  }
  const heatmapDays = Math.min(milestoneDay, 90);


  // HRT adherence — denominator = on-HRT span ∩ last 90 days.
  // Shape fix: fetchHrtBetween returns an Array<{log_date, hrt_taken}>; normalize
  // to a Set of date keys where hrt_taken === true (also handles record shape
  // defensively). Gated at 14 days to avoid misleading % for new starters.
  let adherencePct: number | null = null;
  let adherenceDenomDays = 0;
  let adherenceStartedDaysAgo: number | null = null;
  let startedWithinWindow = false;
  if (onHRT) {
    const takenSet = new Set<string>(
      Array.isArray(hrtMap)
        ? (hrtMap as any[]).filter(r => r?.hrt_taken === true).map(r => r.log_date).filter(Boolean)
        : Object.keys(hrtMap || {}).filter(k => (hrtMap as any)[k] === true),
    );

    const windowStart = parseLocalDateKey(last90Start)!;
    const todayD = parseLocalDateKey(todayKey)!;
    const hrtStartKey = getHrtStartDate(user);
    const hrtStartD = parseLocalDateKey(hrtStartKey || "");
    const effectiveStart = hrtStartD && hrtStartD.getTime() > windowStart.getTime() ? hrtStartD : windowStart;
    startedWithinWindow = !!(hrtStartD && hrtStartD.getTime() > windowStart.getTime());

    if (hrtStartD) {
      adherenceStartedDaysAgo = Math.max(
        0,
        Math.floor((todayD.getTime() - hrtStartD.getTime()) / (1000 * 60 * 60 * 24)),
      );
    }

    const spanKeys: string[] = [];
    const cursor = new Date(effectiveStart);
    while (cursor.getTime() <= todayD.getTime()) {
      spanKeys.push(localDateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    adherenceDenomDays = spanKeys.length;

    if (adherenceDenomDays >= 14) {
      const taken = spanKeys.filter(k => takenSet.has(k)).length;
      adherencePct = Math.round((taken / adherenceDenomDays) * 100);
    }
  }

  const scored = scoreSeries.filter(s => s.score != null && s.loggedCount >= MIN_LOGGED_FOR_INCLUSION) as { dateKey: string; score: number }[];
  const vitalityAvg = scored.length ? Math.round(scored.reduce((s, p) => s + p.score, 0) / scored.length) : null;

  // ---- Render HTML ----
  const headerHtml = `
    <div class="hdr">
      <div style="display:flex;align-items:center;gap:14px;margin-bottom:10px;">
        <div style="width:64px;height:64px;background:#fff;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:30px;font-weight:600;color:${PD_PLUM};letter-spacing:-0.02em;flex-shrink:0;">K<span style="color:${PD_CORAL};">M</span></div>
        <div style="flex:1;">
          <h1>Vitality Report</h1>
          <div class="sub">Recovery, Longevity, Healthspan</div>
        </div>
      </div>
      <div style="font-size:13px;opacity:0.95;">${escapeHtml(name)} · DOB ${escapeHtml(dob)} · Generated ${escapeHtml(today)}</div>
      <div style="font-size:12px;opacity:0.85;margin-top:8px;">A 90-day check-in to support your conversations with your healthcare team.</div>
    </div>`;

  const printInstructionsHtml = `<div class="noprint">
    <strong>To save as PDF:</strong> press <kbd>Ctrl/Cmd + P</kbd> and choose "Save as PDF" — or tap the button below.
    <div style="margin-top:10px;"><button class="btn" onclick="window.print()">Print / Save as PDF</button></div>
  </div>`;

  // Data Confidence — compute once and reuse for the summary helpers.
  const moodDates: string[] = Array.isArray(moodRows)
    ? (moodRows as any[]).map(m => m.log_date).filter(Boolean)
    : [];
  const hrtDates: string[] = Array.isArray(hrtMap)
    ? (hrtMap as any[]).filter(r => r?.hrt_taken).map(r => r.log_date).filter(Boolean)
    : Object.keys(hrtMap || {}).filter(k => (hrtMap as any)[k] === true);
  const confidence = buildDataConfidence({
    onHRTFull: onHRT,
    logsByDate: reportData?.logsByDate || {},
    moodDates,
    hrtDates,
    daysSinceTracking: milestoneDay,
  });

  const sectionsHtml = [
    yourSummarySectionLight({ confidence }),
    dataConfidenceSectionLight({ confidence }),
    clinicianSummarySectionLight({
      user, vitality: vitalityScore, wearableSummary,
      labUploads: labsFetched as LabUploadRow[],
      screenings: screeningsFetched as HealthScreeningRow[],
      directions, confidence, stage, onHRT, hrtType,
    }),
    recoverySection({ vitality: vitalityScore, series: vitalitySeries, stress: stressSummary }),
    longevitySection({ user, summary: wearableSummary }),
    labTrendsSection(labsFetched as LabUploadRow[], stage, onHRT, hrtType),
    screeningsSection(screeningsFetched as HealthScreeningRow[]),
    symptomsSectionLight({ directions, hf: hfSummary, logsByDate: reportData?.logsByDate || {}, hfCountByDate, days: heatmapDays }),
    triggerCorrelationsSection({ data: triggerReport }),
    moodSectionLight({ mood }),
    hrtSectionLight({ user, adherencePct, adherenceDenomDays, adherenceStartedDaysAgo, startedWithinWindow }),
    discussionPointsSectionLight({
      user, vitality: vitalityScore, wearableSummary,
      labUploads: labsFetched as LabUploadRow[],
      screenings: screeningsFetched as HealthScreeningRow[],
      directions, stage, onHRT, hrtType,
      triggerReport,
    }),
    plainEnglishResultsSectionLight({
      user, vitality: vitalityScore, wearableSummary,
      labUploads: labsFetched as LabUploadRow[],
      screenings: screeningsFetched as HealthScreeningRow[],
      directions, mood, confidence, stage, onHRT, hrtType,
    }),
  ].join("\n");

  const footerHtml = `
    <div class="footer">
      <div>${escapeHtml(DISCLAIMER)}</div>
      <div style="margin-top:8px;">Vitality Report generated by KnowMeno on ${escapeHtml(today)}.</div>
    </div>`;

  const yearLabel = new Date().getFullYear();
  const titleStr = `KnowMeno Annual Wellness Summary — ${yearLabel}`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(titleStr)}</title>
    <style>${PDF_STYLES}</style>
  </head><body>
    ${printInstructionsHtml}
    ${headerHtml}
    ${sectionsHtml}
    ${footerHtml}
  </body></html>`;

  finalizeReportWindow(w, html, titleStr);
}
